import { createContext, createSignal, useContext, Show, type JSX, type ParentProps } from "solid-js";
import "./confirm-dialog.css";

export type ConfirmLevel = "info" | "warn" | "danger";

export interface ConfirmOptions {
  title?: string;
  body?: string;
  level?: ConfirmLevel;
  entityName?: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

interface ConfirmApi {
  confirm: (opts?: ConfirmOptions) => Promise<boolean>;
}

const LEVEL_CLASS: Record<ConfirmLevel, string> = {
  info: "confirm--info",
  warn: "confirm--warn",
  danger: "confirm--danger",
};

const Ctx = createContext<ConfirmApi>();

interface DialogState {
  opts: Required<Omit<ConfirmOptions, "entityName">> & { entityName: string };
  resolve: (v: boolean) => void;
}

export function ConfirmProvider(props: ParentProps): JSX.Element {
  const [state, setState] = createSignal<DialogState | null>(null);
  const [typed, setTyped] = createSignal("");

  function close(value: boolean): void {
    const s = state();
    if (!s) return;
    setState(null);
    setTyped("");
    s.resolve(value);
  }

  function onKeyDown(e: KeyboardEvent): void {
    const s = state();
    if (!s) return;
    if (e.key === "Escape") {
      e.preventDefault();
      close(false);
    } else if (e.key === "Enter" && !isDisabled()) {
      e.preventDefault();
      close(true);
    }
  }

  function isDisabled(): boolean {
    const s = state();
    if (!s) return true;
    const requireType = s.opts.level === "danger" && s.opts.entityName.length > 0;
    if (!requireType) return false;
    return typed().trim() !== s.opts.entityName;
  }

  const api: ConfirmApi = {
    confirm: (raw?: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        const level: ConfirmLevel = raw?.level && LEVEL_CLASS[raw.level] ? raw.level : "info";
        const entityName = typeof raw?.entityName === "string" && raw.entityName.trim() ? raw.entityName.trim() : "";
        const opts = {
          title: raw?.title || "Confirmer l'action",
          body: raw?.body || "",
          level,
          entityName,
          confirmLabel: raw?.confirmLabel || (level === "danger" ? "Supprimer" : "Confirmer"),
          cancelLabel: raw?.cancelLabel || "Annuler",
        };
        setTyped("");
        setState((prev) => {
          if (prev) prev.resolve(false);
          return { opts, resolve };
        });
      }),
  };

  return (
    <Ctx.Provider value={api}>
      {props.children}
      <Show when={state()}>
        {(s) => {
          const opts = s().opts;
          const requireType = opts.level === "danger" && opts.entityName.length > 0;
          const eyebrow =
            opts.level === "danger" ? "Action destructive" : opts.level === "warn" ? "Attention" : "Confirmation";
          return (
            <div
              class="confirm-overlay"
              role="dialog"
              aria-modal="true"
              onMouseDown={(e) => {
                if (e.target === e.currentTarget) close(false);
              }}
              onKeyDown={onKeyDown}
              tabIndex={-1}
              ref={(el) => setTimeout(() => el?.focus(), 10)}
            >
              <div class={`confirm-card ${LEVEL_CLASS[opts.level]}`}>
                <header class="confirm-header">
                  <span class="confirm-eyebrow">{eyebrow}</span>
                  <h2 class="confirm-title">{opts.title}</h2>
                </header>
                <div class="confirm-body">{opts.body}</div>
                <Show when={requireType}>
                  <label class="confirm-typing">
                    <span>
                      Tapez <code>{opts.entityName}</code> pour confirmer
                    </span>
                    <input
                      type="text"
                      class="confirm-typing-input"
                      autocomplete="off"
                      spellcheck={false}
                      value={typed()}
                      onInput={(e) => setTyped(e.currentTarget.value)}
                    />
                  </label>
                </Show>
                <footer class="confirm-footer">
                  <button class="btn btn--sm btn--ghost confirm-cancel" onClick={() => close(false)}>
                    {opts.cancelLabel}
                  </button>
                  <button
                    class={`btn btn--sm ${opts.level === "danger" ? "btn--danger" : opts.level === "warn" ? "btn--warn" : "btn--info"} confirm-ok`}
                    disabled={isDisabled()}
                    onClick={() => close(true)}
                  >
                    {opts.confirmLabel}
                  </button>
                </footer>
              </div>
            </div>
          );
        }}
      </Show>
    </Ctx.Provider>
  );
}

/**
 * Hook d'accès au système de confirmation modale.
 *
 * Usage :
 * ```ts
 *   const confirm = useConfirm();
 *   const ok = await confirm.confirm({ title, body, level });
 * ```
 *
 * Note : l'API retourne un objet `{ confirm }` (et non une fonction directe) afin
 * de rester rétro-compatible avec l'ensemble des panneaux opérateur.
 */
export function useConfirm(): ConfirmApi {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useConfirm() hors ConfirmProvider");
  return ctx;
}
